import React, { useEffect, useMemo, useRef, useState } from "react";
import { clientUpdate, loginAnonymously, loginGuest, logout, registrationByEmail } from "../../../../player/js/api";
import { TEST_EMAIL, TYPE_HYBRID } from "../../constants/common";
import { FIELD_KONTUR_FOCUS } from "../../constants/components/authentication";
import { participationTypes } from "../../models/components/AuthenticationModels";
import { getUserInfo } from "../../helpers/user";
import { services } from "@offline/components/SignIn/SocialLogin";
import InputField from "./InputField";
import InputKonturFocus from "./InputKonturFocus";
import UIButton from "../../../../globalComponents/UIComponents/UIButton";
import CButton from "../UI/CButton";

const getRequiredFields = (fields = []) => {
    return fields.filter(({ required }) => required);
};

const areAllFieldsFilled = (value) => {
    if (typeof value === 'object' && value !== null) {
        return Object.values(value).every((fieldValue) => {
            return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
        });
    }

    return value !== null && value !== undefined && value !== '';
};

export default ({
    preview,
    $locale,
    eventKey,
    authRequired,
    allowAnonymousLogin,
    participationTypeSet,
    eventPremoderation,
    email,
    requireAuthPhone,
    fields,
    client,
    participationType,
    participationFormatLabels,
    registrationType,
    localeNameInPascalCase,
    hasRegistrationFields = false,
    isGuestLogin = false,
    onSuccess = () => { },
    onReset,
    onWindowResize = () => { },
}) => {

    const userParticipationType = authRequired ? '' : localStorage.getItem(`participationType_${eventKey}`);
    const isHybridType = registrationType === TYPE_HYBRID && participationType === TYPE_HYBRID;
    const willSelectPaticipationType = participationType === TYPE_HYBRID && !participationTypeSet;

    function getInfoParticipationType() {
        if (participationTypeSet && isGuestLogin) {
            return participationTypeSet;
        }
        if (willSelectPaticipationType && registrationType !== TYPE_HYBRID) {
            return registrationType;
        }
        if (userParticipationType) {
            return userParticipationType;
        }
        if (participationType !== TYPE_HYBRID) {
            return participationType;
        }

        return undefined;
    }

    const [info, setInfo] = useState(() => getUserInfo(client, fields, getInfoParticipationType()));
    const [disabled, setDisabled] = useState(false);
    const [emptyFieldsError, setEmptyFieldsError] = useState(false);
    const [inputErrors, setInputErrors] = useState({});
    const [errors, setErrors] = useState({});
    const [isErrorMessageVisisble, setErrorMessageVisible] = useState(false);
    const formRef = useRef(null);

    const isInputErrors = !Object.keys(inputErrors).every(key => !inputErrors[key]);
    const isKonturFieldDisabled = client && !!client.info?.konturFocusOrg?.organizationInn
        && !!client.info?.konturFocusOrg?.organizationKpp && !!client.info?.konturFocusOrg?.organizationName;

    const participationTypeField = {
        label: $locale.get('auth.login.participationType'),
        type: "select",
        name: "participationType",
        required: true,
        options: (!!participationFormatLabels && !!participationFormatLabels.length)
            ? participationFormatLabels.map(format => ({
                value: format.value,
                label: format['label' + localeNameInPascalCase],
            }))
            : participationTypes($locale),
    };

    const requiredFields = useMemo(() => [willSelectPaticipationType ? participationTypeField : null, ...fields].filter(field => !!field),
        [willSelectPaticipationType, fields]);

    useEffect(() => {
        setInfo(prev => ({ ...prev, ...getUserInfo(client, fields, getInfoParticipationType()) }));
    }, [client, fields])

    useEffect(() => {
        const fields = getRequiredFields(requiredFields);
        const checkEmptyInputs = formRef.current?.checkValidity() && fields.every(field => info[field?.name]);
        setEmptyFieldsError(!checkEmptyInputs);
    }, [formRef.current, requiredFields, info]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (disabled || preview) {
            return;
        }

        if (emptyFieldsError || isInputErrors) {
            requiredFields.forEach(({ name, required }) => {
                if (required && !info[name] && !errors[name] || inputErrors[name]) {
                    setErrors((prevState) => ({ ...prevState, [name]: { hasError: true, name: inputErrors[name] || '' } }));
                }
            });

            return;
        }

        const payload = {
            ...info,
            locale: $locale.name,
            location: window.location.href,
        };

        setDisabled(true);
        try {
            let result;
            if (client) {
                result = await clientUpdate(eventKey, { ...payload });
            } else if (email) {
                result = await registrationByEmail(eventKey, { ...payload, email });
            } else {
                result = await loginGuest(eventKey, { ...payload });
            }

            onSuccess(result);

            if (result.participationType) {
                localStorage.setItem(`participationType_${eventKey}`, result.participationType);
            }
        } catch (e) {
            setDisabled(false);
        }
    }

    const handleAnonymousLogin = async () => {
        if (disabled) {
            return;
        }
        setDisabled(true);
        try {
            const response = await loginAnonymously(eventKey);
            if (response.participationType) {
                localStorage.setItem(`participationType_${eventKey}`, response.participationType);
            }
            onSuccess(response);
        } catch (e) {
            console.error(e);
            setDisabled(false);
        }
    }

    const handleReset = async () => {
        await logout(eventKey);
        if (onReset) {
            onReset();
        }
    }

    const handleSetError = (field) => {
        if (!field.required && !inputErrors[field.name]) {
            return;
        }

        const value = info[field.name] || null;

        if (!info[field.name] || (typeof value === 'string' && !value.trim()) || !value || inputErrors[field.name]) {
            setErrors({ ...errors, [field.name]: { hasError: true, name: inputErrors[field.name] || '' } });
            setErrorMessageVisible(true);
            onWindowResize();
        }
    };

    const infoHandler = (name) => {
        if (client && client?.info[name] && areAllFieldsFilled(client?.info[name])) {
            return;
        }

        return (value, error) => {
            setInfo((prev) => ({ ...prev, [name]: value }));
            setErrors(({ [name]: _, ...errors }) => errors);
            setInputErrors(prev => ({ ...prev, [name]: error }))
        }
    }

    const ResetButton = () => {
        if (!client) {
            return (
                <UIButton
                    type="button"
                    color="transparent"
                    style="link"
                    onClick={() => onReset()}
                >
                    {$locale.get('auth.login.button.changeEmail')}
                </UIButton>
            );
        }

        if (-1 !== services.indexOf(client.type)) {
            return (
                <a onClick={handleReset} className="auth-reset-email-button">{$locale.get('auth.logout')}</a>
            );
        }

        return null;
    }

    return (
        <form
            onSubmit={handleSubmit}
            ref={formRef}
            noValidate
            className="auth-container"
        >
            {(client && !preview) && <p>{$locale.get('registration.hintFillFields')}</p>}
            {(preview || (email && !client?.email)) && <div>
                <InputField
                    label={$locale.get('auth.login.email.email')}
                    type="email"
                    name="email"
                    value={preview ? TEST_EMAIL : (email || client?.email || '')}
                    disabled={true}
                    readOnly={true}
                    required={true}
                    $locale={$locale}
                />
                <ResetButton />
            </div>
            }
            {requiredFields
                // .filter(({name}) => !client || !client.info[name])
                .map((field) => (
                    ((field.name === 'participationType' && isHybridType) || field.name !== 'participationType') &&
                    <div
                        key={field.name}
                        className={errors[field.name] ? 'field-error' : ''}
                    >
                        {field.type === FIELD_KONTUR_FOCUS
                            ? <InputKonturFocus
                                {...field}
                                eventKey={eventKey}
                                $locale={$locale}
                                value={info[field.name]}
                                disabled={disabled || isKonturFieldDisabled}
                                readOnly={isKonturFieldDisabled}
                                error={errors[field.name]?.hasError}
                                errorName={errors[field.name]?.name ? $locale.get(errors[field.name].name) : ''}
                                onChange={infoHandler(field.name)}
                                onBlur={() => handleSetError(field)}
                            />
                            : <InputField
                                {...field}
                                value={info[field.name]}
                                defaultValue={info[field.name]}
                                hint={(field.name === 'phone' && requireAuthPhone) ? $locale.get('auth.login.phone.hint') : null}
                                disabled={disabled || (client && client.info[field.name])}
                                readOnly={client && client.info[field.name]}
                                defaultOptionTitle={$locale.get('input.select.defaultTitle')}
                                error={errors[field.name]?.hasError}
                                errorName={errors[field.name]?.name ? $locale.get(errors[field.name].name) : ''}
                                isOptionsDisabled={field.name === 'participationType' && !!userParticipationType}
                                onChange={infoHandler(field.name)}
                                onBlur={() => handleSetError(field)}
                                $locale={$locale}
                            />
                        }
                    </div>
                ))
            }
            {
                (isErrorMessageVisisble && emptyFieldsError) &&
                <p className="error-message">* {$locale.get('auth.login.fillRequiredFields')}</p>
            }
            <div className="auth-submit-buttons">
                <UIButton
                    type="submit"
                    size="large"
                    disabled={disabled}
                    style={(isInputErrors || emptyFieldsError) ? 'not-active' : 'primary'}
                    onClick={() => setErrorMessageVisible(isInputErrors || emptyFieldsError)}
                >
                    {
                        $locale.get((!email && !client?.email)
                            ? (hasRegistrationFields ? 'auth.login.button.register' : 'auth.login.button.next')
                            : (eventPremoderation ? 'auth.login.button.sendRequest' : 'auth.login.button.register')
                        )
                    }
                </UIButton>
                {allowAnonymousLogin &&
                    <div>
                        {$locale.get('common.or')}
                        &nbsp;&nbsp;
                        <CButton
                            blank
                            label={$locale.get('auth.login.button.loginAnonymously')}
                            className={'comdi-button--text comdi-button--text-primary'}
                            onClick={handleAnonymousLogin}
                        />
                    </div>
                }
            </div>
        </form>
    );
}
